import React from "react";
import { IconListItem, Icomoon, PriceInCurrency } from "../..";
import { ItineraryPricingProps } from "..";
import { isAfter, now, formatDate } from "@travellocal/utils/dateHelpers";
import { subtract } from "@travellocal/utils/priceHelpers";
import "./PriceBreakdown.scss";

type Props = Pick<ItineraryPricingProps, "lineItems" | "totalPrice" | "totalPaid" | "balanceDueDate" | "prepayment">;

export const PriceBreakdown: React.FC<Props> = ({
  lineItems,
  totalPrice,
  totalPaid,
  balanceDueDate,
  prepayment,
}) => {

  const hasRemainingBalance = totalPaid != null && totalPaid.price > 0 && totalPaid.price < totalPrice.price;
  const isBalanceOverdue = balanceDueDate != null && isAfter(now(), balanceDueDate);

  return (
    <div className="itin-price-breakdown">
      <div className="itin-price-breakdown__items">
        {lineItems?.map(lineItem => (
          <React.Fragment key={lineItem.id}>
            <IconListItem className="itinerary-pricing__item is-marginless" icomoon={Icomoon.checkmark3}>
              <div className="columns is-mobile is-multiline is-marginless">
                <div className="column is-12-mobile is-paddingless">
                  {lineItem.name}
                </div>
                <div className="column has-text-right is-paddingless">
                  <strong><PriceInCurrency displayForCustomer priceInCurrency={lineItem.unitPrice} /></strong>
                </div>
                <div className="column is-narrow is-paddingless">
                  <strong>&nbsp;x {lineItem.quantity}</strong>
                </div>
              </div>
            </IconListItem>
            <hr />
          </React.Fragment>
        ))}
      </div>

      {prepayment && (
        <>
          <div className="columns is-mobile is-marginless">
            <div className="column">
              <h3 className="is-marginless">Increased deposit amount</h3>
            </div>
            <div className="column is-narrow">
              <strong className="heading--3">
                <PriceInCurrency displayForCustomer priceInCurrency={prepayment.amount} />
              </strong>
            </div>
          </div>

          <p className="itin-price-breakdown__reason">{prepayment.reason}</p>

          <hr />
        </>
      )}

      <div className="columns is-mobile is-marginless">
        <div className="column">
          <h3 className="is-marginless">Total trip cost</h3>
          {totalPrice.price === 0 && (
            <p>This itinerary does not have a price.</p>
          )}
        </div>
        <div className="column is-narrow">
          <strong className="heading--3">
            {totalPrice.price > 0
              ? <PriceInCurrency displayForCustomer priceInCurrency={totalPrice} />
              : <span className="money-string">TBC</span>
            }
          </strong>
        </div>
      </div>

      {(hasRemainingBalance && totalPaid != null) && (
        <>
          <hr />

          <div className="columns is-mobile is-marginless itin-price-breakdown__total-paid">
            <div className="column">
              <h3 className="is-marginless">Total paid to date</h3>
            </div>
            <div className="column is-narrow">
              <strong className="heading--3">
                <PriceInCurrency displayForCustomer priceInCurrency={totalPaid} />
              </strong>
            </div>
          </div>


          <div className="columns is-mobile is-marginless is-multiline">
            <div className="column">
              <h3 className="is-marginless">Remaining balance</h3>

              {balanceDueDate != null && (
                isBalanceOverdue
                  ? (
                    <p className="is-marginless">
                      Payment is now overdue. Please pay as soon as possible.
                    </p>
                  )
                  : (
                    <p className="is-marginless">
                      Due by{" "}
                      <time dateTime={balanceDueDate.value}>{formatDate(balanceDueDate, "do MMM YYYY")}</time>
                    </p>
                  )
              )}
            </div>
            <div className="column is-narrow">
              <strong className="heading--3">
                <PriceInCurrency displayForCustomer priceInCurrency={subtract(totalPrice, totalPaid)} />
              </strong>
            </div>
          </div>

        </>
      )}
    </div>
  );
}
